/**
 * useSalesRoomLabel — the single source of truth for what a "PLR" is CALLED
 * in the UI. Residential tenants say "Sales Room"; commercial (office /
 * warehousing) tenants lease, so they say "Leasing Room".
 *
 * Driven by the tenant-level `industry_type` (TenantContext.isResidential),
 * which is fetched once per session — so the label is stable app-wide and
 * never per-asset. Use this everywhere instead of hardcoding "PLR".
 *
 *   const { label, labelPlural } = useSalesRoomLabel();
 *   <h1>{labelPlural}</h1>                       // "Sales Rooms"
 *   toast(`${label} created`)                    // "Sales Room created"
 *   `your ${labelLower}`                         // "your sales room"
 */

import { useTenant } from '../contexts/TenantContext';

function build(isResidential) {
    const label = isResidential ? 'Sales Room' : 'Leasing Room';
    const labelPlural = isResidential ? 'Sales Rooms' : 'Leasing Rooms';
    return {
        label,
        labelPlural,
        labelLower: label.toLowerCase(),
        labelPluralLower: labelPlural.toLowerCase(),
    };
}

// Session snapshot of the resolved label. Defaults to the residential
// ("Sales Room") wording and is refreshed by every hook call below, so
// module-level helpers that can't call a hook (label maps, timeline
// formatters) can still read the tenant-correct wording via
// salesRoomLabelSnapshot(). By the time those helpers run inside a render,
// the owning component has already called the hook this same render.
let _snapshot = build(true);

export function useSalesRoomLabel() {
    const { isResidential } = useTenant();
    _snapshot = build(isResidential);
    return _snapshot;
}

// Non-hook accessor for module-level code. Returns the last resolved label.
export function salesRoomLabelSnapshot() {
    return _snapshot;
}

export default useSalesRoomLabel;
